import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Instagram, Facebook, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import { CartSheet } from "@/components/CartSheet";

const LINKS = [
  { href: "#menu", label: "Menu" },
  { href: "#how", label: "How to Order" },
  { href: "#pay", label: "Payment" },
  { href: "#reviews", label: "Reviews" },
  { href: "#feed", label: "Feed" },
  { href: "#about", label: "About" },
  { href: "#order", label: "Order" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/90 backdrop-blur-md shadow-sm border-b border-line" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={BUSINESS.logo} alt="KitchenBox logo" className="h-12 w-12 rounded-full object-cover ring-1 ring-black/5 shadow-sm" />
          <span className="flex flex-col leading-none">
            <span className={`font-serif text-xl sm:text-2xl font-bold ${scrolled ? "text-brand" : "text-white"}`}>KitchenBox</span>
            <span className={`text-[10px] sm:text-xs tracking-[0.25em] uppercase ${scrolled ? "text-inkmuted" : "text-cream/80"}`}>Iraqi & Middle Eastern</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-semibold hover:text-brand transition-colors ${scrolled ? "text-ink" : "text-white"}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#order"
            data-testid="nav-order-now"
            className="bg-brand text-white hover:bg-brand-hover transition-colors rounded-full px-6 py-2.5 text-sm font-bold shadow-md"
          >
            Order Now
          </a>
          <CartSheet light={!scrolled} />
        </div>

        <div className="md:hidden flex items-center gap-1">
          <CartSheet light={!scrolled} />
          <button
            className={`p-2 ${scrolled ? "text-ink" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <MenuIcon size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-cream border-b border-line overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-semibold text-ink hover:text-brand"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 pt-2">
                <a href="#order" onClick={() => setOpen(false)} className="text-center bg-brand text-white rounded-full py-3 font-bold">
                  Order on the Site
                </a>
                <a href={BUSINESS.instagramUrl} target="_blank" rel="noreferrer" className="text-center rounded-full py-3 font-bold text-white bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5]">
                  Order on Instagram
                </a>
              </div>
              <div className="flex gap-4 text-inkmuted pt-1">
                <a href={BUSINESS.instagramUrl} target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={20} /></a>
                <a href={BUSINESS.facebookUrl} target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook size={20} /></a>
                <a href={`tel:${BUSINESS.phoneRaw}`} aria-label="Call"><Phone size={20} /></a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
